<template>
  <BaseModal
    :show="show"
    title="High school graduation date"
    max-width="636"
    :persistent="!viewingAs"
    :show-close="viewingAs"
    header-style="font-size: 24px; line-height: 28px; letter-spacing: -0.72px; color: #191919;"
    header-class="font-weight-bold px-6 pt-6 pb-4 d-flex align-center justify-space-between"
    content-class="px-6 pt-0 pb-6"
    @update:show="$emit('update:show', $event)"
    @close="$emit('close')"
  >
    <div
      v-if="viewingAs"
      class="hs-grad-date__view-as d-flex align-center ga-2 pa-4 mb-4"
      role="note"
      data-testid="hs-grad-date-view-as-note"
    >
      <v-icon icon="mdi-information-outline" size="18" color="#191919" aria-hidden="true" />
      <span>
        You’re viewing as {{ learnerName || 'this learner' }}. They’ll see this until they answer it. Close it to keep
        going. Nothing you enter here is saved.
      </span>
    </div>

    <p class="hs-grad-date__intro mb-6">To continue, select the month and year you graduated or expect to graduate.</p>

    <div class="d-flex flex-column flex-sm-row ga-6 mb-6">
      <FormField label="Month" :required="!declined" class="hs-grad-date__field" v-slot="{ id }">
        <v-select
          :id="id"
          v-model="month"
          :items="monthOptions"
          placeholder="Enter month"
          menu-icon="mdi-chevron-down"
          :menu-props="menuProps"
          :list-props="{ density: 'compact' }"
          variant="outlined"
          density="compact"
          hide-details
          :disabled="declined || saving"
          data-testid="hs-grad-date-month"
        />
      </FormField>
      <FormField label="Year" :required="!declined" class="hs-grad-date__field" v-slot="{ id }">
        <v-select
          :id="id"
          v-model="year"
          :items="yearOptions"
          placeholder="Enter year"
          menu-icon="mdi-chevron-down"
          :menu-props="menuProps"
          :list-props="{ density: 'compact' }"
          variant="outlined"
          density="compact"
          hide-details
          :disabled="declined || saving"
          data-testid="hs-grad-date-year"
        />
      </FormField>
    </div>

    <label class="hs-grad-date__option mb-6" data-testid="hs-grad-date-decline">
      <input v-model="declined" type="checkbox" :disabled="saving" />
      <span>I prefer not to provide this information</span>
    </label>

    <UiButton
      variant="gold"
      :disabled="!canSave"
      :loading="saving"
      class="hs-grad-date__save"
      data-testid="hs-grad-date-save"
      @click="handleSave"
    >
      Save
    </UiButton>
  </BaseModal>
</template>

<script>
import { defineComponent } from 'vue';
import { BaseModal, Button as UiButton, FormField } from './ui/index.js';
import { GRADUATION_YEAR_SPAN, MONTH_OPTIONS, SHORT_MENU_PROPS, graduationYearOptions } from '../utils/hsGraduation';

export default defineComponent({
  name: 'HsGraduationDateModal',

  components: {
    BaseModal,
    UiButton,
    FormField
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },
    // View-As: closable, and the parent never saves (see App.vue).
    viewingAs: {
      type: Boolean,
      default: false
    },
    learnerName: {
      type: String,
      default: ''
    },
    saving: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:show', 'save', 'close'],

  data() {
    return {
      month: null,
      year: null,
      declined: false
    };
  },

  watch: {
    // Start blank each time, so one learner's answer never shows for the next (View-As).
    show(visible) {
      if (visible) {
        this.month = null;
        this.year = null;
        this.declined = false;
      }
    }
  },

  computed: {
    monthOptions() {
      return MONTH_OPTIONS;
    },
    yearOptions() {
      return graduationYearOptions(new Date(), { yearsBack: GRADUATION_YEAR_SPAN });
    },
    menuProps() {
      return SHORT_MENU_PROPS;
    },
    canSave() {
      return !this.saving && (this.declined || Boolean(this.month && this.year));
    }
  },

  methods: {
    handleSave() {
      if (!this.canSave) return;
      this.$emit('save', this.declined ? { declined: true } : { graduationDate: `${this.year}-${this.month}` });
    }
  }
});
</script>

<style scoped>
.hs-grad-date__intro {
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.48px;
  color: #191919;
}

.hs-grad-date__field {
  flex: 1 1 0;
  min-width: 0;
}

.hs-grad-date__field :deep(.form-field__label) {
  font-weight: 700;
}

.hs-grad-date__view-as {
  background: #e8e8e8;
  border-radius: 4px;
  font-size: 16px;
  line-height: 24px;
  color: #000;
}

/* Figma "Checkbox": 24px square, flush left, 8px to a 16/24 label. A native input (like the
   registration radios) avoids v-checkbox's 40px centred hit area, which insets the box. The border
   is gray-3, not Figma's #d0d0d0, for 3:1 contrast on white. */
.hs-grad-date__option {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  color: #191919;
  cursor: pointer;
}

.hs-grad-date__option input {
  appearance: none;
  -webkit-appearance: none;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  margin: 0;
  border: 1px solid var(--asu-grayscale-gray-3);
  background: #fff center / 16px no-repeat;
  cursor: pointer;
}

.hs-grad-date__option input:checked {
  border-color: #191919;
  background-color: #191919;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M3 8.5l3 3 7-7' fill='none' stroke='%23fff' stroke-width='2'/%3E%3C/svg%3E");
}

.hs-grad-date__option input:focus {
  outline: none;
}

.hs-grad-date__option input:focus-visible {
  outline: 2px solid #191919;
  outline-offset: 2px;
}

.hs-grad-date__option:has(input:disabled) {
  cursor: not-allowed;
  opacity: 0.5;
}

.hs-grad-date__option input:disabled {
  cursor: not-allowed;
}

/* High contrast themes drop the custom fill, so fall back to the native box and tick. */
@media (forced-colors: active) {
  .hs-grad-date__option input,
  .hs-grad-date__option input:checked {
    appearance: auto;
    -webkit-appearance: auto;
    background: none;
  }
}

.hs-grad-date__save {
  min-width: 112px;
}

:deep(.v-field--variant-outlined .v-field__outline__start),
:deep(.v-field--variant-outlined .v-field__outline__end) {
  border-color: #d0d0d0;
}
</style>
